import { authErrorHandler } from '@/services/error/authHandler.js';

/**
 * Universal method for retrieving user connections.
 * @param {Object} params - Request parameters.
 * @param {string} params.userId - User ID.
 * @param {string} params.type - Connection type (followers/following).
 * @param {number} [params.offset=0] - Offset for pagination.
 * @param {number} [params.limit=20] - Limit for pagination.
 * @param {boolean} [params.withUnreadPosts] - Whether to return only users with unread posts.
 * @returns {Promise<Object|null>} - User followers/following data or null in case of error.
 */
export const fetchUserConnections = async (params = {}) => {
  try {
    if (!params.userId || params.userId === 'null') {
      console.error('userId is invalid:', params.userId);
      return null;
    }
    if (!params.type || params.type === 'null') {
      console.error('type is invalid:', params.type);
      return null;
    }

    const { userId, type, ...queryParams } = params;

    const filteredParams = Object.fromEntries(
      Object.entries({
        ...queryParams,
        offset: queryParams.offset || 0,
        limit: queryParams.limit || 15,
        withUnreadPosts: queryParams.withUnreadPosts,
      }).filter(([_, value]) => value !== undefined),
    );

    if (!userId || userId === 'null') {
      console.error('userId is invalid:', new Error().stack);
      return null;
    }

    const searchParams = new URLSearchParams(filteredParams).toString();
    const url = `/api/users/${userId}/${type}?${searchParams}`;

    const response = await fetch(url, {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (response.status === 401 || response.status === 403) {
      authErrorHandler(response);
      return null;
    }

    const data = await response.json();

    if (response.ok) {
      return data;
    } else {
      console.error(`Error fetching ${type}:`, data);
      return null;
    }
  } catch (error) {
    console.error(`Error during ${params.type} API call:`, error);
    return null;
  }
};

/**
 * Universal method for managing subscriptions (follow/unfollow).
 * @param {string} userId - User ID.
 * @param {string} action - Action type ('follow' or 'unfollow').
 * @param {boolean} [negative] - For follow action: whether this is a "skeptical" subscription.
 * @returns {Promise<void>} - Data of the user being followed/unfollowed or null in case of error.
 */
export const manageFollowState = async (userId, action, negative = false) => {
  if (!userId || userId === 'null') {
    console.error('userId is invalid:', new Error().stack);
    return null;
  }
  const url = `/api/users/${userId}/${action}`;
  const options = action === 'follow' ? { body: JSON.stringify({ negative }) } : {};

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: options.body || undefined,
    });

    if (response.status === 401 || response.status === 403) {
      authErrorHandler(response);
      return null;
    }

    const data = await response.json();

    if (response.ok) {
      return data;
    } else {
      console.error(`Error performing "${action}" action:`, data);
      return null;
    }
  } catch (error) {
    console.error(`Error performing "${action}" action:`, error);
    throw error;
  }
};
